import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
  MdKeyboardArrowUp,
} from "react-icons/md";
import { cn } from "../../lib/utils";
import CustomButton from "../button/CustomButton";

const MenuLinkItem = React.memo(({ section, label, currentHash, onClick }) => (
  <a
    href={`#${section}`}
    onClick={() => onClick(`#${section}`)}
    className={cn(
      "menu-link relative text-black lg2:text-lg text-base py-1 after:absolute after:h-[4px] after:bg-black after:w-0 after:left-0 after:bottom-[-3px] after:transition-all after:duration-300 hover:after:w-full whitespace-nowrap snap-center",
      currentHash === `#${section}` ? "after:w-full" : ""
    )}
    aria-label={label}
  >
    {label}
  </a>
));

// Componente de botão de scroll memoizado
const ScrollButton = React.memo(({ direction, onClick, className }) => (
  <button
    className={cn(
      "absolute top-1/2 transform -translate-y-1/2 bg-gray-200 p-1 rounded-full shadow z-10",
      direction === "left" ? "left-0" : "right-0",
      className
    )}
    onClick={() => onClick(direction)}
    aria-label={`Scroll para ${direction === "left" ? "esquerda" : "direita"}`}
  >
    {direction === "left" ? (
      <MdKeyboardArrowLeft className="text-xl" />
    ) : (
      <MdKeyboardArrowRight className="text-xl" />
    )}
  </button>
));

// Componente de banner fixo memoizado
const FixedBanner = React.memo(({ isFixed, onButtonClick }) => (
  <div
    className={cn(
      "w-screen bg-white opacity-100 border-t border-solid border-gray-400 px-5 lg:px-10 py-[1.4rem] flex-col justify-center items-center fixed bottom-0 left-0 right-0 z-50 transition-opacity duration-200 ease-linear",
      isFixed ? "fixed" : "opacity-0 pointer-events-none -z-50"
    )}
  >
    <div className="w-full max-w-[85rem] mx-auto flex flex-row justify-between items-center gap-5">
      <div className="flex flex-2 justify-start font-bold">
        <p className="text-left max-md:text-sm">
          Cadastre-se agora e <br className="lg:hidden" /> aproveite o
          benefício.
        </p>
      </div>
      <div className="flex flex-1 justify-end">
        <CustomButton
          textButton="Economize já"
          blueVariant
          onClick={onButtonClick}
        />
      </div>
    </div>
  </div>
));

// Componente principal
const MenuMiddleVB = () => {
  // Estados
  const [hash, setHash] = useState("");
  const [isFixed, setIsFixed] = useState(false);
  const [menuHeight, setMenuHeight] = useState(0);
  const [isMenuScrollable, setIsMenuScrollable] = useState(false);

  // Refs
  const menuRef = useRef(null);
  const placeholderRef = useRef(null);
  const observerRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const resizeObserverRef = useRef(null);

  // Lista de seções memoizada
  const sections = useMemo(
    () => ["beneficios", "cadastrar", "sobreThopen", "duvidas"],
    []
  );

  // Configurar Intersection Observer para as seções
  useEffect(() => {
    const handleIntersection = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const newHash = `#${entry.target.id}`;
          setHash((prev) => {
            if (prev !== newHash) {
              window.history.replaceState(null, null, newHash);
              return newHash;
            }
            return prev;
          });
        }
      });
    };

    observerRef.current = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: "0px",
      threshold: 0.5,
    });

    sections.forEach((section) => {
      const element = document.getElementById(section);
      if (element) observerRef.current.observe(element);
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [sections]);

  useEffect(() => {
    const updateMenuDimensions = (entries) => {
      for (const entry of entries) {
        setMenuHeight(entry.contentRect.height);
        setIsMenuScrollable(
          entry.target.scrollWidth > entry.target.clientWidth
        );
      }
    };

    resizeObserverRef.current = new ResizeObserver(updateMenuDimensions);

    if (menuRef.current) {
      resizeObserverRef.current.observe(menuRef.current);
    }

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, []);

  // Manipulador de scroll com throttling
  const handleScroll = useCallback(() => {
    if (scrollTimeoutRef.current) return;

    scrollTimeoutRef.current = requestAnimationFrame(() => {
      if (placeholderRef.current) {
        const menuTop = placeholderRef.current.offsetTop;
        setIsFixed(window.scrollY > menuTop);
      }
      scrollTimeoutRef.current = null;
    });
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef.current) {
        cancelAnimationFrame(scrollTimeoutRef.current);
      }
    };
  }, [handleScroll]);

  // Função para rolar o menu horizontalmente
  const scrollMenu = useCallback((direction) => {
    if (menuRef.current) {
      const scrollAmount = direction === "left" ? -200 : 200;
      menuRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  }, []);

  // Função para rolar para o topo
  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Função para atualizar o hash
  const updateHash = useCallback((newHash) => {
    setHash(newHash);
  }, []);

  // Memoizando os links do menu para evitar re-renderizações desnecessárias
  const menuLinks = useMemo(
    () =>
      sections.map((section) => {
        const labels = {
          beneficios: "Benefícios",
          cadastrar: "Como cadastrar",
          sobreThopen: "Sobre a Thopen",
          duvidas: "Dúvidas frequentes",
        };

        return (
          <MenuLinkItem
            key={section}
            section={section}
            label={labels[section]}
            currentHash={hash}
            onClick={updateHash}
          />
        );
      }),
    [sections, hash, updateHash]
  );

  return (
    <>
      <div
        ref={placeholderRef}
        style={{ height: isFixed ? `${menuHeight}px` : "0px" }}
      />
      <div
        ref={menuRef}
        className={cn(
          "top-0 left-0 right-0 z-50 w-screen  px-2 py-5 bg-white flex items-center",

          isFixed
            ? "shadow-lg fixed top-0 left-0 right-0 z-50 py-2"
            : "relative"
        )}
      >
        <div
          className={cn(
            "w-full max-w-[85rem] px-2 py-5 mx-auto flex justify-between items-center gap-1 overflow-x-auto scroll-smooth snap-x snap-mandatory z-10",
            isFixed ? "py-4" : "py-5"
          )}
        >
          <button
            className={cn(
              "hidden lg:flex items-center text-black gap-1 text-lg font-semibold opacity-0 pointer-events-none transition-opacity duration-200 ease-linear hover:opacity-50",
              isFixed ? "opacity-100 pointer-events-auto" : ""
            )}
            onClick={scrollToTop}
            aria-label="Topo"
          >
            <MdKeyboardArrowUp className="text-2xl" /> Topo
          </button>

          <div className="flex-1 relative flex justify-center">
            {isMenuScrollable && (
              <ScrollButton direction="left" onClick={scrollMenu} />
            )}
            <div className="flex items-center gap-8 px-4">{menuLinks}</div>
            {isMenuScrollable && (
              <ScrollButton direction="right" onClick={scrollMenu} />
            )}
          </div>
        </div>

        <FixedBanner isFixed={isFixed} onButtonClick={scrollToTop} />
      </div>
    </>
  );
};

// Definindo nomes de exibição para os componentes memoizados
MenuLinkItem.displayName = "MenuLinkItem";
ScrollButton.displayName = "ScrollButton";
FixedBanner.displayName = "FixedBanner";

// Exportando o componente principal memoizado
export default React.memo(MenuMiddleVB);
